import type { ReactNode } from 'react';
import cx from 'clsx';
import { Button } from '@repo/design-system-kit/components/button/button';
import IconCheckmarkOutlined from '@repo/monochrome-icons/icon-checkmark-outlined';
import styles from './welcome-landing.module.css';

interface WelcomeLandingAction {
  label: ReactNode;
  onPress: () => void;
}

interface WelcomeLandingProps {
  featuresList?: ReactNode[];
  illustration?: ReactNode;
  logosBar?: ReactNode;
  primaryAction: WelcomeLandingAction;
  secondaryAction?: WelcomeLandingAction;
  socialProofLabel?: ReactNode;
  subtitle?: ReactNode;
  title: ReactNode;
  topbar?: ReactNode;
}

export function WelcomeLanding({
  featuresList,
  illustration,
  logosBar,
  primaryAction,
  secondaryAction,
  socialProofLabel,
  subtitle,
  title,
  topbar,
}: WelcomeLandingProps): JSX.Element {
  return (
    <div className={styles.wrapper}>
      {topbar}
      <div className={styles.container}>
        <div className={styles.main}>
          <div className={styles.left}>
            <h1 className="heading-page mb-16" data-test-welcome-title>
              {title}
            </h1>

            {subtitle ? (
              <p className="body-standard mb-32" data-test-welcome-subtitle>
                {subtitle}
              </p>
            ) : null}

            {featuresList && featuresList.length > 0 ? (
              <ul className={cx('mb-32 body-prominent', styles.list)}>
                {featuresList.map((feature, index) => (
                  <li
                    className={cx('mb-16', styles.listItem)}
                    data-test-welcome-list-item={index}
                    // oxlint-disable-next-line react/no-array-index-key
                    key={index}
                  >
                    <span className={styles.listIcon}>
                      <IconCheckmarkOutlined />
                    </span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            ) : null}

            <div className={styles.actions}>
              {secondaryAction ? (
                <Button
                  data-test-welcome-secondary-cta
                  onPress={secondaryAction.onPress}
                  variant="secondary"
                >
                  {secondaryAction.label}
                </Button>
              ) : null}
              <Button
                className={styles.primaryCta}
                data-test-welcome-cta
                onPress={primaryAction.onPress}
              >
                {primaryAction.label}
              </Button>
            </div>
          </div>

          {illustration ? (
            <div className={styles.illustration} data-test-welcome-illustration>
              {illustration}
            </div>
          ) : null}
        </div>

        {socialProofLabel || logosBar ? (
          <div className={styles.partners}>
            {socialProofLabel ? (
              <span className={styles.logoTitle} data-test-welcome-logo-title>
                {socialProofLabel}
              </span>
            ) : null}
            {logosBar}
          </div>
        ) : null}
      </div>
    </div>
  );
}
